import { useEffect, useState } from "@modica-library/react19";
import { Button, Card, Dropdown, Textarea } from "../ui";
import { ModicaIcon } from "@modica-library/icons/react";
import { getStats, type Stats } from "../lib/api";
import { readDemoSession } from "../lib/session";
import platformPreview from "../assets/platform-preview.png";
import welcomeDeco from "../assets/logged-user-bg.png";

/** /dashboard — welcome, support, platform preview + the summary column. */
export function DashboardHome() {
  const session = readDemoSession();
  const [stats, setStats] = useState<Stats>({ queued: 0, sent: 0, notDelivered: 0, responses: 0, monthTotal: 0 });

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
  }, []);

  return (
    <>
      <div className="col-mid">
        <div className="welcome">
          <img className="welcome__deco" src={welcomeDeco} alt="" aria-hidden="true" />
          <h2>
            Welcome to your Demo,
            <br />
            {session.firstName}!
          </h2>
        </div>

        <Card
          title={
            <span className="card-title--icon">
              <ModicaIcon iconName="headset_mic" color="black" decorative />
              Contact Support Team
            </span>
          }
        >
          <div className="support">
            <Dropdown
              options={[
                { label: "General Inquiry", value: "general" },
                { label: "Billing", value: "billing" },
                { label: "Technical", value: "tech" },
              ]}
            />
            <Textarea label="Inquiry" rows={3} />
            <div>
              <Button>Submit enquiry</Button>
            </div>
          </div>
        </Card>

        <Card title="Our Platform">
          <div className="video">
            <img className="video__img" src={platformPreview} alt="Our Platform preview" />
          </div>
        </Card>
      </div>

      <div className="col-right">
        <div className="summary__head">
          <h2>Summary</h2>
        </div>

        <div className="summary__filters">
          <Dropdown
            options={[
              { label: "Last 30 Days", value: "30" },
              { label: "Last 7 Days", value: "7" },
            ]}
          />
          <Dropdown
            options={[
              { label: "SMS Units", value: "sms" },
              { label: "Credits", value: "credits" },
            ]}
          />
        </div>

        <h3 className="section-heading">Daily Message Count</h3>
        <Card>
          <MessageChart />
        </Card>

        <div className="stats">
          <Stat label="Queued" note="Waiting to send out" value={stats.queued} />
          <Stat label="Sent" note="Sent out from platform" value={stats.sent} />
          <Stat label="Not Delivered" note="Didn’t reach recipient" value={stats.notDelivered} />
          <Stat label="Responses" note="Messages sent in from people" value={stats.responses} />
        </div>

        <Card
          title={
            <span className="card-title--icon">
              <ModicaIcon iconName="description" color="black" decorative />
              Usage Report
            </span>
          }
        >
          <div className="usage">
            <p>
              You’ve sent <strong className="usage__num">{stats.monthTotal}</strong> messages this month!
            </p>
            <a href="#" className="usage__link">
              View Volume Report
            </a>
          </div>
        </Card>
      </div>
    </>
  );
}

function Stat({ label, note, value }: { label: string; note: string; value: number }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      <div className="stat__note">{note}</div>
    </div>
  );
}

function MessageChart() {
  const days = ["July 15", "July 16", "July 17", "July 18", "July 19", "July 20", "July 21", "July 22"];
  const yTicks = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1, 0.5, 0];
  return (
    <div className="chart">
      <div className="chart__ylabel">Unit Count</div>
      <div className="chart__yaxis">
        {yTicks.map((n) => (
          <span key={n}>{n === 0 ? "0" : n.toFixed(1)}</span>
        ))}
      </div>
      <div className="chart__plot">
        <svg viewBox="0 0 560 180" preserveAspectRatio="none" className="chart__svg">
          {yTicks.map((n, i) => (
            <line key={n} x1="0" x2="560" y1={i * 18} y2={i * 18} stroke="#eef2f6" strokeWidth="1" />
          ))}
          <line x1="0" y1="179" x2="560" y2="179" stroke="var(--ezi-blue)" strokeWidth="2" />
        </svg>
        <div className="chart__xaxis">
          {days.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
